import React, { useMemo, useState } from 'react';
import { Box, Inscription, PencilIcon, PlusIcon, RemoveIcon, Status } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize, toNumber } from 'lodash';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import { formatCurrency } from '@/utils';

import { ContributionAmountSideKey } from '../../ContributionEmployeesSettings/ContributionEmployeesSettings.types';
import formatTaxType from '../../Contributions/formatTaxType';
import {
  AccountWrapper,
  HoveredIcon,
  RowItemWrapper,
  StyledText,
} from '../ContributionActivitiesList.styles';
import { EditDataModalPopup, FormValues } from '../EditDataModalPopup';
import { HEADERS_EDIT, HEADERS_VIEW, HeadersKey } from '../HeaderOfList';
import {
  ChangedContributionRecord,
  useChangedContributionRecordsStore,
} from '../useChangedContributionRecords.store';

import AmountInput from './AmountInput';
import ContributionDateInput from './ContributionDateInput';
import { ViewedValue } from './ViewedValue';

interface Props {
  contributionActivity: ChangedContributionRecord;
  isEditMode: boolean;
}

export const ContributionRecord: React.FC<Props> = ({ contributionActivity, isEditMode }) => {
  const [isEditModalShown, setIsEditModalShown] = useState(false);
  const headers = useMemo(() => (isEditMode ? HEADERS_EDIT : HEADERS_VIEW), [isEditMode]);

  const { changeRecord } = useChangedContributionRecordsStore();

  const isGreyBackground = useMemo(
    () => contributionActivity?.isDeleted || contributionActivity?.disabled,
    [contributionActivity?.isDeleted, contributionActivity?.disabled]
  );
  const backgroundColor = useMemo(() => (isGreyBackground ? 'border2' : 'canvas'), [isGreyBackground]);
  const borderColor = useMemo(() => (isGreyBackground ? 'border1' : 'border2'), [isGreyBackground]);

  return (
    <>
      <AccountWrapper
        data-testid={`ContributionRecord_Wrapper_${contributionActivity?.recordId}`}
        pad={{ horizontal: 'spacing16', vertical: 'spacing12' }}
        round="container2Round"
        background={backgroundColor}
        border={{ size: 'small', color: borderColor }}
      >
        <RowItemWrapper direction="row">
          {Object.entries(headers).map(([key, item]) => (
            <Box
              data-testid={`ContributionRecord_${key}_${contributionActivity?.recordId}`}
              width={{ width: item.width, max: item.width }}
              height={{ min: '40px' }}
              key={key}
              justify="center"
            >
              {key === HeadersKey.name ? (
                <>
                  <StyledText
                    title={contributionActivity?.employeeFullName}
                    lineHeight="20px"
                    color="textBody"
                  >
                    {contributionActivity?.employeeFullName || EMPTY_FIELD_VALUE}
                  </StyledText>

                  {contributionActivity?.subsidiaryName ? (
                    <Box
                      background="border1"
                      round="snackbarRound"
                      justify="center"
                      align="center"
                      margin={{ top: 'spacing2' }}
                      pad={{ horizontal: 'spacing4' }}
                      width="fit-content"
                    >
                      <StyledText
                        title={contributionActivity.subsidiaryName}
                        lineHeight="20px"
                        color="textBody"
                      >
                        {contributionActivity.subsidiaryName}
                      </StyledText>
                    </Box>
                  ) : null}
                </>
              ) : null}

              {key === HeadersKey.account ? (
                <Inscription truncate>{contributionActivity?.planCode}</Inscription>
              ) : null}

              {key === HeadersKey.eeAmount ? (
                isEditMode ? (
                  <AmountInput
                    amountKey={ContributionAmountSideKey.EmployeeContributionAmount}
                    record={contributionActivity}
                  />
                ) : (
                  <ViewedValue
                    errorMessage={contributionActivity?.amountsError}
                    testId={ContributionAmountSideKey.EmployeeContributionAmount}
                    title={
                      contributionActivity?.disabled
                        ? '-'
                        : contributionActivity?.employeeContributionAmount
                          ? (formatCurrency(
                              toNumber(contributionActivity?.employeeContributionAmount)
                            ) as string)
                          : EMPTY_FIELD_VALUE
                    }
                  />
                )
              ) : null}

              {key === HeadersKey.erAmount ? (
                isEditMode ? (
                  <AmountInput
                    amountKey={ContributionAmountSideKey.EmployerContributionAmount}
                    record={contributionActivity}
                  />
                ) : (
                  <ViewedValue
                    errorMessage={contributionActivity?.amountsError}
                    testId={ContributionAmountSideKey.EmployerContributionAmount}
                    title={
                      contributionActivity?.disabled
                        ? '-'
                        : contributionActivity?.employerContributionAmount
                          ? (formatCurrency(
                              toNumber(contributionActivity?.employerContributionAmount)
                            ) as string)
                          : EMPTY_FIELD_VALUE
                    }
                  />
                )
              ) : null}

              {key === HeadersKey.date ? (
                isEditMode ? (
                  <ContributionDateInput record={contributionActivity} />
                ) : (
                  <ViewedValue
                    errorMessage={contributionActivity?.dateError}
                    testId="contributionDateOn"
                    title={
                      contributionActivity?.contributionDateOn
                        ? dayjs(contributionActivity?.contributionDateOn).format(DEFAULT_DATE_FORMAT)
                        : EMPTY_FIELD_VALUE
                    }
                  />
                )
              ) : null}

              {key === HeadersKey.taxYear ? (
                <ViewedValue
                  errorMessage={contributionActivity?.taxYearError}
                  testId="taxYear"
                  title={contributionActivity?.taxYear || EMPTY_FIELD_VALUE}
                />
              ) : null}

              {key === HeadersKey.taxType ? (
                <Inscription>
                  {contributionActivity?.taxType
                    ? formatTaxType(contributionActivity?.taxType)
                    : EMPTY_FIELD_VALUE}
                </Inscription>
              ) : null}

              {key === HeadersKey.status ? (
                <Box direction="row" justify="end" align="center">
                  <Inscription truncate>{capitalize(contributionActivity?.processingStatus)}</Inscription>
                  <Box pad={{ left: 'spacing8' }}>
                    <Status circleOnly status={contributionActivity?.processingStatus} />
                  </Box>
                </Box>
              ) : null}

              {key === HeadersKey.memo ? (
                <Inscription truncate title={contributionActivity?.memo}>
                  {contributionActivity?.memo || EMPTY_FIELD_VALUE}
                </Inscription>
              ) : null}

              {key === HeadersKey.editActions ? (
                <Box direction="row" margin={{ left: 'spacing24' }} justify="end">
                  {contributionActivity?.isDeleted ? (
                    <Box
                      width="40px"
                      height="40px"
                      round="container2Round"
                      justify="center"
                      align="center"
                      background="canvas"
                      onClick={() =>
                        changeRecord({
                          ...contributionActivity,
                          isDeleted: false,
                        })
                      }
                      data-testid={`${key}_${contributionActivity?.recordId}_add`}
                    >
                      <PlusIcon color="iconAccent" size="small" />
                    </Box>
                  ) : (
                    <>
                      <HoveredIcon data-testid={`${key}_${contributionActivity?.recordId}_edit`}>
                        <PencilIcon
                          color="iconPrimary"
                          width="24px"
                          onClick={() => setIsEditModalShown(true)}
                        />
                      </HoveredIcon>
                      <HoveredIcon
                        margin={{ left: 'spacing16' }}
                        onClick={() =>
                          changeRecord({
                            ...contributionActivity,
                            isDeleted: true,
                          })
                        }
                        data-testid={`${key}_${contributionActivity?.recordId}_remove`}
                      >
                        <RemoveIcon color="iconPrimary" width="24px" />
                      </HoveredIcon>
                    </>
                  )}
                </Box>
              ) : null}
            </Box>
          ))}
        </RowItemWrapper>
      </AccountWrapper>

      {isEditModalShown ? (
        <EditDataModalPopup
          isVisible
          close={() => setIsEditModalShown(false)}
          record={contributionActivity}
          onSave={(data?: FormValues) => {
            changeRecord({
              ...contributionActivity,
              employeeContributionAmount: data?.employeeContributionAmount || '',
              employerContributionAmount: data?.employerContributionAmount || '',
              contributionDateOn: data?.contributionDateOn || '',
              taxYear: data?.taxYear || '',
              ...(data?.taxType ? { taxType: data.taxType } : {}),
              memo: data?.memo || '',
            });
          }}
        />
      ) : null}
    </>
  );
};
